import {
	createContext,
	type PropsWithChildren,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react';

import { CookieService } from '~/services/actions/cookie-actions';
import LocalStorageService from '~/services/actions/localstorage-actions';
import {
	TRACKING_ACTIONS,
	TRACKING_CATEGORIES,
	TRACKING_EVENTS,
} from '~/services/actions/tracking-actions';
import ConfigService, { type Config } from '~/stores/configuration';
import { BETSLIPCOOKIE, BETSLIPDATA } from '~/stores/constants';
import { ODDS_FORMAT } from '~/types';

import { useMyOCContext } from '../myoc-context';
import { useRepubContext } from '../repub-context';
import { useServicesContext } from '../services-context';

import { useBetslipUtils } from './hooks/use-betslip-utils';
import { useOddsUtils } from './hooks/use-odds-utils';
import {
	type Bet,
	type BetslipProviderData,
	type BetslipProviderProps,
	type BetslipState,
	betslipType,
	type BookieOdds,
	type CallbackFn,
	type IntBetslipCookie,
	type OddsFormatIs,
} from './types';

export const BetslipContext = createContext<BetslipProviderData>(
	undefined as any as BetslipProviderData,
);
BetslipContext.displayName = 'BetslipContext';

export const BetslipProvider = ({
	children,
	userOddsFormat,
	...props
}: PropsWithChildren<BetslipProviderProps>) => {
	const { repub } = useRepubContext();
	const { myocData } = useMyOCContext();
	const { countryCode, linkActions, state, trackingActions } =
		useServicesContext();

	const cookieService = useMemo(() => new CookieService(), []);
	const localStorageService = useMemo(() => new LocalStorageService(), []);

	// NOTE: no idea wtf this is as it seems to be a relic
	// from legacy but has been migrated for feature parity
	// until we investigate and likely remove
	const _configService: Config = useMemo(
		() => ConfigService.getInstance(repub).getConfig(),
		[repub],
	);

	const oddsFormatIs: OddsFormatIs = useMemo(
		() => ({
			American: userOddsFormat === ODDS_FORMAT.AMERICAN,
			Decimal: userOddsFormat === ODDS_FORMAT.DECIMAL,
			Fractional: userOddsFormat === ODDS_FORMAT.FRACTIONAL,
		}),
		[userOddsFormat],
	);

	// NOTE: betslipUtils and oddsUtils encompass
	// helpers which do not mutate local state
	const betslipUtils = useBetslipUtils({ oddsFormatIs });
	const oddsUtils = useOddsUtils();

	const [added, setAdded] = useState<boolean>(false);
	const [betslip, setBetslip] = useState<Bet[]>([]);
	const [bookieOdds, setBookieOdds] = useState<BookieOdds[]>([]);
	const [cookie, setCookie] = useState<IntBetslipCookie>({
		intBetslipCookie: '',
	});
	const [loading, setLoading] = useState<boolean>(true);
	const [open, setOpen] = useState<boolean>(false);
	const [selectedBookie, setSelectedBookie] = useState<BookieOdds | null>(null);
	const [stake, setStake] = useState<number>(10);

	const callbacksRef = useRef<Record<string, CallbackFn[]>>({});
	const shouldResetBetslipOnCloseRef = useRef<boolean>(false);

	const betslipState = useMemo<BetslipState>(
		() => ({
			added,
			betslip,
			betIds: betslip.map(b => b.betId),
			bookieOdds,
			cookie,
			count: betslip.length,
			loading,
			open,
			selectedBookie,
			stake,
		}),
		[added, betslip, bookieOdds, cookie, loading, open, selectedBookie, stake],
	);

	const toggleAdded = useCallback((val?: boolean): void => {
		if (typeof val !== 'undefined') {
			setAdded(val);
			return;
		}

		setAdded(o => !o);
	}, []);

	const toggleBetslip = useCallback((val?: boolean): void => {
		if (typeof val !== 'undefined') {
			setOpen(val);
			return;
		}

		setOpen(o => !o);
	}, []);

	const changeStake = useCallback((stake: number): void => setStake(stake), []);

	// NOTE: it is essential we pass betslip data here
	// as this helper is used to initialise local state
	const storeBetslipData = useCallback(
		(data: Bet[]): void => {
			const { calculateOddsAndBestBookie } = betslipUtils;

			const newBookieOdds = calculateOddsAndBestBookie(
				data,
				data.length === 1 ? betslipType.SINGLE : betslipType.MULTIPLE,
			);

			if (newBookieOdds.length < 1) {
				setBetslip([]);
				return;
			}

			let newSelectedBookie = null;

			if (data.length === 1) {
				const bestOddsBookmakers: string[] = newBookieOdds
					.filter(odds => odds.oddsDecimal === data[0].bestOddsDecimal)
					.map(o => o.bookmakerCode);

				const bestOddsBookieCode = bestOddsBookmakers?.[0];

				newSelectedBookie = newBookieOdds.find(
					odd => odd.bookmakerCode === bestOddsBookieCode,
				);
			} else if (newBookieOdds.length > 0) {
				let bookiePosition =
					newBookieOdds[0].bookmakerType === 'exchange' ? 1 : 0;

				// if the only odds are for an exchange, pick that one
				if (
					newBookieOdds.length == 1 &&
					newBookieOdds[0].bookmakerType === 'exchange'
				) {
					newSelectedBookie = newBookieOdds[0];
				} else {
					newSelectedBookie = selectedBookie
						? newBookieOdds.find(
								odd =>
									selectedBookie &&
									odd.bookmakerCode === selectedBookie.bookmakerCode,
							)
						: newBookieOdds[bookiePosition];
				}
			} else {
				setBookieOdds([]);
			}

			setBookieOdds(newBookieOdds);
			setBetslip(data);
			if (newSelectedBookie) setSelectedBookie(newSelectedBookie);
		},
		[betslipUtils, selectedBookie],
	);

	const fetchBetslipData = useCallback((): void => {
		const { getBetslipAPI, getBetslipCookieArray } = betslipUtils;

		const betslipCookieArray = getBetslipCookieArray();

		const numberOfBets = betslipCookieArray.length;
		const betslipHasMultipleBets = numberOfBets > 1;
		const betslipHasSingleBet = numberOfBets === 1;

		if (betslipHasMultipleBets || betslipHasSingleBet) {
			const betIds = betslipCookieArray.map(bet => parseInt(bet[0], 10));
			getBetslipAPI(betIds, countryCode, state, (status, res) => {
				if (status.success && res.data[BETSLIPDATA]) {
					storeBetslipData(res.data[BETSLIPDATA].bets);
				}
				setLoading(false);
			});
		} else {
			setBetslip([]);
			setLoading(false);
		}

		setAdded(false);
	}, [betslipUtils, countryCode, state, storeBetslipData]);

	const addOrRemoveFromBetslip = useCallback(
		(
			betArray: any[],
			matchBetIds?: any[],
			bookie?: string,
			betOdds?: string,
			betName?: string,
			betLocation?: string,
			subeventName?: string,
			marketName?: string,
			eventName?: string,
		): void => {
			setLoading(true);

			const { getBetslipCookieArray, getLocalStorageBetslip, trackBetSlip } =
				betslipUtils;
			const cookieBetIdArray = getBetslipCookieArray();
			const localStorageBetslip = getLocalStorageBetslip();

			let betIndex: number;
			const trackingObject = {
				event: TRACKING_EVENTS.ADD_TO_BETSLIP,
				eventCategory: TRACKING_CATEGORIES.BETTING,
				eventAction: TRACKING_ACTIONS[TRACKING_EVENTS.ADD_TO_BETSLIP],
				eventLabel: bookie,
				bookieCode: bookie,
				betOdds,
				betName,
				betLocation,
				subeventName,
				marketName,
				eventName,
			};

			for (let i = 0; i < betArray.length; i++) {
				// Add or remove the ocBetId arg depending on its presence in the betIdArray.
				betIndex = cookieBetIdArray
					.map(item => item[0])
					.indexOf(betArray[i].betId.toString());

				if (betIndex === -1) {
					trackingObject.eventLabel = bookie;
					// Push the bet into the array with a 1 for "selected".
					cookieBetIdArray.push([betArray[i].betId.toString(), '1']);
					// Add selectedBookie so it is retrievable via getBetSelectedBookieFromLocalStore()
					betArray[i].selectedBookie = bookie;
					localStorageBetslip[betArray[i].betId] = betArray[i];

					if (typeof matchBetIds !== 'undefined') {
						// eslint-disable-next-line array-callback-return
						matchBetIds.map(matchBet => {
							if (matchBet !== betArray[i].betId) {
								const removedBetIndex = cookieBetIdArray
									.map(item => item[0])
									.indexOf(matchBet.toString());
								if (removedBetIndex !== -1) {
									cookieBetIdArray.splice(removedBetIndex, 1);
									// Remove from localStorage
									delete localStorageBetslip[matchBet];
								}
							}
						});
					}

					if (cookieBetIdArray.length === 1) {
						setOpen(true);
						trackBetSlip(false, betArray);
					}
				} else {
					trackingObject.event = TRACKING_EVENTS.REMOVE_BET;
					trackingObject.eventAction =
						TRACKING_ACTIONS[TRACKING_EVENTS.REMOVE_BET];
					trackingObject.eventLabel = '';
					// Remove from cookie
					cookieBetIdArray.splice(betIndex, 1);

					// Remove from localStorage
					delete localStorageBetslip[betArray[i].betId];
				}
				trackingActions.eventTracking(trackingObject);
			}

			if (cookieBetIdArray.length === 0) {
				setOpen(false);
			}

			setAdded(true);

			// Store mini betslip in localStorage to make odds avaialbe for estimated betslip odds.
			localStorageService.select('intBetslip').set(localStorageBetslip);

			// Select and update cookie and store
			const newCookieString = cookieService
				.select('intBetslip')
				.setArrayString(cookieBetIdArray);

			setCookie({ [BETSLIPCOOKIE]: newCookieString });

			// ensure we update state
			fetchBetslipData();
		},
		[
			betslipUtils,
			trackingActions,
			localStorageService,
			cookieService,
			fetchBetslipData,
		],
	);

	const removeBetFromBetslip = useCallback(
		(betId: number, disabled?: boolean, bet?: Bet): void => {
			const tempBetslip: Array<any> = [];
			for (let i = 0; i < betslip.length; i++) {
				let betWasRemoved = true;
				if (betslip[i].betId !== betId) {
					betWasRemoved = false;
					tempBetslip.push(betslip[i]);
				}

				if (betWasRemoved) {
					addOrRemoveFromBetslip(
						[{ betId }],
						[betId],
						selectedBookie?.bookmakerCode,
						selectedBookie?.oddsDecimal.toString(),
						bet?.name,
						'betslip',
						bet?.subeventName,
						bet?.marketName,
						'n/a',
					);
				}
			}
			if (!disabled) {
				let bookieExists = false;
				tempBetslip.forEach(bet => {
					bookieExists = bet.odds.some(
						(odds: { bookmakerCode: string }) =>
							odds.bookmakerCode === (selectedBookie?.bookmakerCode ?? ''),
					);
				});

				if (!bookieExists) {
					setSelectedBookie(null);
					fetchBetslipData();
				}
			}

			callbacksRef.current.removeBet?.forEach(callback => callback(betId));
		},
		[addOrRemoveFromBetslip, betslip, selectedBookie, fetchBetslipData],
	);

	const handleClearBetslip = useCallback((): void => {
		// 1. tracking
		const trackingData = {
			event: TRACKING_EVENTS.BET_SLIP_CLEAR,
			eventCategory: TRACKING_CATEGORIES.BETTING,
			eventAction: TRACKING_ACTIONS[TRACKING_EVENTS.BET_SLIP_CLEAR],
			eventLabel: '',
		};
		trackingActions.eventTracking(trackingData);

		trackingData.event = TRACKING_EVENTS.BET_SLIP_CLOSE;
		trackingData.eventAction = TRACKING_ACTIONS[TRACKING_EVENTS.BET_SLIP_CLOSE];
		trackingActions.eventTracking(trackingData);

		// 2. close betslip and setup callbacks
		shouldResetBetslipOnCloseRef.current = true;
		setOpen(false);
	}, [trackingActions]);

	useEffect(() => {
		if (open || !shouldResetBetslipOnCloseRef.current) return;
		shouldResetBetslipOnCloseRef.current = false;

		// 1. callbacks
		callbacksRef.current.clearBetslip?.forEach(callback => callback());

		// 2. clear betslip
		const bets = betslipUtils.getBetslipCookieArray();
		const modifiedBets = bets.map(bet => ({ betId: Number(bet[0]) }));
		addOrRemoveFromBetslip(modifiedBets);

		// 3. init state
		setBookieOdds([]);
		setBetslip([]);
	}, [open, betslipUtils, addOrRemoveFromBetslip]);

	// NOTE: we pass through the bookie here,
	// rather than pulling from state, as it's
	// possilbe to click signup for other
	// bookies in list, i.e. non-best
	const handleClickoutToPlaceBet = useCallback(
		(bookie: string, betIds: number[]): void => {
			try {
				// 1. clickout
				const { CLICKOUT_URL, OC_API_KEY } = window.ENV;
				const apiKey = OC_API_KEY[repub as keyof typeof OC_API_KEY];
				const betUrl = `${CLICKOUT_URL}/splash/clickout?betIds=${betIds.join('%7C')}&bookieCode=${bookie}&apiKey=${apiKey}`;
				linkActions.clickout(betUrl, bookie, 'bet', true);

				// 2. tracking
				const betIdsCount = betIds.length;

				trackingActions.eventTracking({
					event: TRACKING_EVENTS.BET_PLACED,
					eventCategory: TRACKING_CATEGORIES.BETTING,
					eventAction: TRACKING_ACTIONS[TRACKING_EVENTS.BET_PLACED],
					eventLabel:
						betIdsCount === 1 ? 'Place Single Bet' : 'Place Multi Bet',
					bet: {
						stake: '',
						accumulator: betIdsCount > 1 ? 'yes' : 'no',
						onsite: 'no',
						saleConfirm: 'no',
						custref: myocData?.myOCUser?.email,
						bookie,
					},
				});
			} catch (error) {
				if (error instanceof Error) {
					console.error(error);
					throw new Error(error.message);
				} else {
					throw error;
				}
			}
		},
		[repub, myocData, linkActions, trackingActions],
	);

	const selectBookie = useCallback(
		(idx: number): void => {
			setSelectedBookie(bookieOdds[idx]);

			trackingActions.eventTracking({
				event: TRACKING_EVENTS.CHANGE_BOOKIE_SELECTION,
				eventCategory: TRACKING_CATEGORIES.BETTING,
				eventAction: TRACKING_ACTIONS[TRACKING_EVENTS.CHANGE_BOOKIE_SELECTION],
			});
		},
		[trackingActions, bookieOdds],
	);

	const hasFetchedBetslipDataOnMountRef = useRef(false);
	useEffect(() => {
		if (hasFetchedBetslipDataOnMountRef.current) return;
		hasFetchedBetslipDataOnMountRef.current = true;
		fetchBetslipData();
	}, [fetchBetslipData]);

	const value = useMemo(
		() => ({
			callbacksRef,
			children,
			oddsFormatIs,
			userOddsFormat,
			...betslipUtils,
			...oddsUtils,
			...betslipState,
			toggleAdded,
			toggleBetslip,
			changeStake,
			fetchBetslipData,
			storeBetslipData,
			handleClearBetslip,
			handleClickoutToPlaceBet,
			addOrRemoveFromBetslip,
			removeBetFromBetslip,
			selectBookie,
		}),
		[
			callbacksRef,
			children,
			oddsFormatIs,
			userOddsFormat,
			betslipUtils,
			oddsUtils,
			betslipState,
			toggleAdded,
			toggleBetslip,
			changeStake,
			fetchBetslipData,
			storeBetslipData,
			handleClearBetslip,
			handleClickoutToPlaceBet,
			addOrRemoveFromBetslip,
			removeBetFromBetslip,
			selectBookie,
		],
	);

	return (
		<BetslipContext.Provider value={value} {...props}>
			{children}
		</BetslipContext.Provider>
	);
};

export const useBetslipContext = () => {
	const context = useContext(BetslipContext);
	if (!context) {
		throw new Error(
			'useBetslipContext can only be used inside of BetslipProvider',
		);
	}

	return context;
};
